import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AddPersonModal from "~components/AddPersonModal";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useDeletePerson, useFetchPersons } from "~pages/Persons/hooks";
import { defaultPersonsExportFieldNames, personsExportFields } from "~pages/Persons/PersonListPage/constants/exportFields.ts";
import { useExportPersons } from "~pages/Persons/PersonListPage/hooks/useExportPersons.ts";
import { useGetPersonTableColumns } from "~pages/Persons/PersonListPage/hooks/useGetPersonTableColumns.tsx";

import { useGetPersonTableFilters } from "~pages/Persons/PersonListPage/hooks/useGetPersonTableFilters.tsx";
import { useImportPersons } from "~pages/Persons/PersonListPage/hooks/useImportPersons.ts";
import type { SearchFilterParams } from "~pages/Persons/PersonListPage/types/searchFilterParams.ts";

const PersonListPage = () => {
  const { formatMessage } = useIntl();
  const { sport, customerId, organizationId } = useParams();
  const columns = useGetPersonTableColumns();
  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.persons.listPage;
  const dataTableImportConfiguration = uiConfiguration.pages.persons.listPage.importConfig;
  const location = useLocation();
  const filterConfig = useGetPersonTableFilters();
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const [isAddPersonModalOpen, setIsAddPersonModalOpen] = useState<boolean>(false);

  const paginationConfig = useHandlePagination(50);
  const [sortBy, setSortBy] = useState<string | null>(null);

  const handlePersonAdded = (localFullName: string) => {
    paginationConfig.resetPagination();
    setSearchText(localFullName);
  };

  const listParams = useMemo<SearchFilterParams>(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      nameFullLocalContains: searchText,
      sortBy: sortBy || "",
      status: filterConfig.currentFilter.status?.value,
      nationality: filterConfig.currentFilter.nationality?.value,
      gender: filterConfig.currentFilter.gender?.value,
      includeTotalCount: "true",
    }),
    [paginationConfig, searchText, filterConfig.currentFilter, sortBy],
  );

  const { data, isLoading, refetch } = useFetchPersons(listParams, sortBy !== null);
  const { mutateAsync: deletePerson, isPending: isDeletePending } = useDeletePerson();
  const navigate = useNavigate();

  const { mutateAsync: startImporting } = useImportPersons();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportPersons();

  const totalCount = data?.totalCount || undefined;

  const getNavigateUrl = (personId: string) => `/${customerId}/admin/${sport}/${organizationId}/persons/manage/${personId}`;

  return (
    <>
      <AddPersonModal open={isAddPersonModalOpen} handleClose={() => setIsAddPersonModalOpen(false)} onAdd={handlePersonAdded} />
      <DataTable
        title={formatMessage({ id: "personListPage.title" })}
        columns={columns}
        handleRefresh={() => {
          refetch();
        }}
        filtersConfig={filterConfig}
        isDataLoading={isLoading}
        rowActions={{
          base: {
            delete: {
              onClick: (row) => deletePerson(row.personId),
              loading: isDeletePending,
            },
            view: {
              onClick: (row) => navigate(getNavigateUrl(row.personId)),
            },
          },
        }}
        handleAdd={() => setIsAddPersonModalOpen(true)}
        addButtonIntlKey={"person"}
        rowKey={"personId"}
        rowNameKey={"nameFullLocal"}
        dataSource={data?.data || []}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.persons`}
        columnSortConfig={{
          sorterLocalStorageKey: `tables.settings.sort.persons`,
          defaultSorter: {
            columnKey: "nameFullLocal",
            order: "ascend",
          },
          onSortChange: setSortBy,
        }}
        searchConfig={{
          handleSearch: setSearchText,
          searchText: searchText,
        }}
        exportConfig={{
          handleExport: startExport,
          loading: isExportPending,
          defaultFields: defaultPersonsExportFieldNames,
          groupedFields: personsExportFields,
          localStorageKey: "export.persons",
        }}
        importConfig={{
          ...dataTableImportConfiguration,
          handleImport: startImporting,
        }}
        permissionNames={[PermissionNames.PERSON]}
        dataTableUiConfiguration={dataTableUiConfiguration}
        paginationConfig={{ ...paginationConfig, total: totalCount }}
      />
    </>
  );
};

export default PersonListPage;
